import { shift } from '@floating-ui/react';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import {
  faCaretDown,
  faCaretUp,
  faInfoCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePrevious } from '@reach/utils';
import type { Node } from '@react-types/shared';
import { FieldHelperProps, FieldInputProps, useFormikContext } from 'formik';
import {
  CSSProperties,
  FC,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
  FocusEvent,
  ReactElement,
} from 'react';
import { AriaListBoxOptions, useButton, useComboBox } from 'react-aria';
import mergeRefs from 'react-merge-refs';
import {
  ComboBoxState,
  Item,
  ListState,
  useComboBoxState,
} from 'react-stately';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { Alert } from 'src/components/feedback/alert';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import { FormFieldProps } from 'src/components/form/form-field-props';
import { useFormMode } from 'src/components/form/form-mode-context';
import { FormOption } from 'src/components/form/form-option';
import { InnerAddon } from 'src/components/form/inner-addon';
import { SimpleInput } from 'src/components/form/input';
import { Label } from 'src/components/form/label';
import { SelectOption } from 'src/components/form/select';
import {
  ListBox,
  ListBoxContainer,
  ListItem,
} from 'src/components/form/select/components/list-box';
import { Popover } from 'src/components/form/select/components/popover';
import { RemoveButton } from 'src/components/form/select/components/remove-button';
import { SelectToggle } from 'src/components/form/select/components/select-toggle';
import { Separator } from 'src/components/form/select/components/separator';
import { applySortBy, SortByOptions } from 'src/components/form/select/utils';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Stack } from 'src/components/layout/stack';
import { Tooltip } from 'src/components/overlay/tooltip';
import { Hint } from 'src/components/text/hint';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useFloating } from 'src/hooks/use-floating';
import { useId } from 'src/hooks/use-id';
import { useOnChange } from 'src/hooks/use-on-change';
import { object, ObjectSchemaDefinition, string } from 'yup';

type SearchableSelectValue<Value extends string> =
  | FormOption<Value | ''>
  | undefined;

export function getSearchableSelectSchema<T extends object, C = object>(
  valueSchema = string(),
  fields?: ObjectSchemaDefinition<T, C>
) {
  return object({
    label: string().required(),
    value: valueSchema.required(),
    ...fields,
  })
    .required()
    .optional(); // if you write `optional()` after `required()`, then `undefined` is a valid input value, but not a valid output value
}

type SingleSelectProps<Value extends string = string> = FormFieldProps & {
  hideLabel?: boolean;
  stacked?: boolean;
  inline?: boolean;
  setSearchValue: (value: string) => void;
  onChange?: (value: Value | '') => void;
  onFocus?: (event: FocusEvent<HTMLInputElement, Element>) => void;
  /**
   * The search value always reflects the input value of our search field.
   */
  searchValue: string;
  /**
   * As long as no search result exists (e.g. request is still pending) we
   * default to either an empty array or an array with the provided initial value.
   */
  searchResult?: FormOption<Value | ''>[];
  disabled?: boolean;
  dataTestId?: string;
  minSearchValueLength: number;
  truncated?: boolean;
  placeholder?: string;
  initialOpen?: boolean;
  leftAddon?: ReactElement;
  children?: (childrenProps: ComboboxChildrenProps) => ReactNode;
  sortBy?: SortByOptions;
  popoverStyle?: CSSProperties;
} & (
    | {
        /**
         * The search runs completely on the client.
         * The input value (the `searchValue`) is not debounced.
         * The items which will be searched don't need to be loaded, so the search can run immediately.
         */
        mode: 'client-search';
        pending?: undefined;
        lastSearchQuery?: undefined;
      }
    | {
        /**
         * The search runs completely on the client.
         * The input value (the `searchValue`) is debounced. That's why we need `lastSearchQuery` to know what
         * was the last query that was used for a search.
         * The items which will be searched don't need to be loaded, so the search can run immediately.
         */
        mode: 'client-search-debounced-input';
        pending?: undefined;
        lastSearchQuery: string | null;
      }
    | {
        /**
         * The search runs completely on the client.
         * The input value (the `searchValue`) is not debounced.
         * The items which will be searched need to be loaded, before the search can run.
         */
        mode: 'client-search-server-data';
        pending: boolean;
        lastSearchQuery?: undefined;
      }
    | {
        /**
         * The search runs completely on the server which means we get the `searchResult` asynchronously
         * and we need to provide a `lastSearchQuery` to know what was the last query that was used for a search.
         *
         * The input value (the `searchValue`) is very likely debounced (- currently we don't differentiate here).
         */
        mode: 'server-search';
        pending: boolean;
        lastSearchQuery: string | null;
      }
  );

export function SearchableSingleSelect<Value extends string = string>(
  props: SingleSelectProps<Value>
) {
  const { minTablet } = useBreakpoints();
  const {
    name,
    label,
    minSearchValueLength,
    pending = false,
    placeholder = 'Type to Search',
    stacked = !minTablet,
    dataTestId,
    inline,
    lastSearchQuery,
    hideLabel = false,
    searchResult,
    truncated,
    searchValue,
    setSearchValue,
    disabled,
    initialOpen,
    hint,
    info,
    leftAddon,
    popoverStyle,
    onChange,
    onFocus,
    children,
    sortBy,
    markAsRequired,
  } = props;

  const mode = useFormMode();

  const [field, _showError, _error, helper] = useCustomField<
    SearchableSelectValue<Value>
  >(name, label);

  // our actual `field` is a complex object (`FormOption`), but for validation errors
  // we're only interested in `FormOption['value']`
  const [_, showError, error] = useCustomField<Value | '' | undefined>(
    `${name}.value`,
    label
  );

  // we don't care about this. we just don't want unhandled `defined` validation errors to pop up for labels
  // as they aren't important
  useCustomField<string | undefined>(`${name}.label`, label);

  const formGroup = useFieldGroup();

  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  const id = useId();

  const fieldId = formGroup?.id || id;

  const fieldItem = (
    <FieldItem>
      <Combobox<Value>
        initialOpen={initialOpen}
        pending={pending}
        name={name}
        placeholder={placeholder}
        minLength={minSearchValueLength}
        searchResult={searchResult}
        id={fieldId}
        label={label}
        dataTestId={dataTestId}
        lastSearchQuery={lastSearchQuery}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        error={showError}
        field={field}
        helper={helper}
        disabled={disabled}
        truncated={truncated}
        onChange={onChange}
        onFocus={onFocus}
        leftAddon={leftAddon}
        popoverStyle={popoverStyle}
        children={children}
        sortBy={sortBy}
        keepSearchValue={mode === 'search'}
        keepOpenIfFocused={mode === 'search'}
      />
      {hint && (
        <Hint disabled={props.disabled} mode="formInput" children={hint} />
      )}
      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
          type="selectable"
        />
      )}
    </FieldItem>
  );

  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;
  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked} inline={inline}>
      <Label
        htmlFor={fieldId}
        error={showError}
        data-testid={`${field.name}Label`}
      >
        {displayLabel}

        {info && (
          <>
            &nbsp;
            <Tooltip content={info}>
              {(targetProps) => (
                <FontAwesomeIcon {...targetProps} icon={faInfoCircle} />
              )}
            </Tooltip>
          </>
        )}
      </Label>

      {fieldItem}
    </FieldLayout>
  ) : (
    fieldItem
  );
}

export type ComboboxChildrenProps = {
  listBoxOptions: AriaListBoxOptions<SelectOption<string | null>>;
  listBoxRef: RefObject<HTMLElement>;
  searchState: SearchState;
  comboBoxState: ComboBoxState<SelectOption<string | null>>;
  name: string;
  truncated?: boolean;
};

type ComboboxProps<Value extends string = string> = {
  dataTestId?: string;
  id: string;
  pending?: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  truncated?: boolean;
  minLength: number;
  lastSearchQuery?: string | null;
  error?: boolean;
  disabled?: boolean;
  searchResult?: FormOption<Value | ''>[];
  placeholder: string;
  field: FieldInputProps<SearchableSelectValue<Value>>;
  helper: FieldHelperProps<SearchableSelectValue<Value>>;
  initialOpen?: boolean;
  onChange?: (value: Value | '') => void;
  onFocus?: (event: FocusEvent<HTMLInputElement, Element>) => void;
  leftAddon?: ReactElement;
  children?: (childrenProps: ComboboxChildrenProps) => ReactNode;
  sortBy?: SortByOptions;
  popoverStyle?: CSSProperties;
  keepSearchValue?: boolean;
  keepOpenIfFocused?: boolean;
} & FormFieldProps;

function Combobox<Value extends string = string>(props: ComboboxProps<Value>) {
  const {
    name,
    truncated,
    searchValue,
    lastSearchQuery,
    id,
    field,
    helper,
    placeholder,
    error,
    minLength,
    label,
    dataTestId,
    disabled,
    pending,
    initialOpen,
    setSearchValue,
    onChange,
    onFocus,
    leftAddon,
    popoverStyle,
    children = (props) => <SelectPopover {...props} />,
    sortBy = 'label',
    keepSearchValue,
    keepOpenIfFocused,
    markAsRequired,
  } = props;

  const { initialValues } =
    useFormikContext<Record<string, SearchableSelectValue<Value>>>();
  const initialValue = initialValues[field.name];

  // in general innerSearchResult is always synced with the actual searchResult we
  // get via props. there is just one difference: on blur we'll restore our previous state.
  // if this happens, we need to be able to set the searchResult immidiately (it could arrive
  // async in case of server requests).
  const [innerSearchResult, setInnerSearchResult] = useState(
    () => props.searchResult ?? (initialValue ? [initialValue] : [])
  );
  useEffect(
    () =>
      setInnerSearchResult(
        props.searchResult ?? (initialValue ? [initialValue] : [])
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.searchResult]
  );

  const buttonRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listBoxRef = useRef(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const items = applySortBy(sortBy, innerSearchResult);

  const comboxBoxChildren = (item: FormOption<Value | ''>) => {
    return (
      <Item
        key={item.value}
        textValue={item.label}
        data-testid={`${dataTestId ?? name}-option-${item.value}`}
      >
        {item.label}
      </Item>
    );
  };

  const state = useComboBoxState({
    defaultInputValue: searchValue,
    inputValue: searchValue,
    children: comboxBoxChildren,
    allowsEmptyCollection: true,
    defaultSelectedKey: initialValue?.value ?? '',
    selectedKey: field.value?.value ?? '',
    items,
    defaultFilter: () => true,
    onSelectionChange(key) {
      // combox box will trigger a selection change, if the input value is cleared.
      // we'll ignore this, as we'd like to restore the selection on blur.
      if (!state.inputValue && !key) return;
      if (!key) return;

      // if the selected key matches our value, the selection hasn't actually changed
      if (key === field.value?.value) return;

      const selectedItem = innerSearchResult.find(
        (option) => option.value === key
      );
      helper.setValue(selectedItem);
    },
  });

  // attention: selectedItem can actually be undefined.
  // the react-aria typings are not correct.
  const selectedItem = state.selectedItem as
    | Node<SearchableSelectValue<Value>>
    | undefined;

  useEffect(() => {
    if (initialOpen) {
      state.setFocused(true);
      state.setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // in general we want to update the selected key and search value
  // whenever the field value changes, but we ignore the very first
  // field value in case there is an initial search value set (but no initial
  // value -> see next useEffect)
  useOnChange(
    () => {
      state.setSelectedKey(field.value?.value ?? '');
      onChange?.(field.value?.value ?? '');
      if (keepSearchValue) return;
      setSearchValue(field.value?.label ?? '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    field.value?.value
  );

  // whenever an initial value was set, we update the search value so
  // it reflects the initial label
  useEffect(() => {
    if (!initialValue) return;
    setSearchValue(initialValue.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevIsFocused = usePrevious(state.isFocused);

  useEffect(() => {
    if (state.isFocused) return;
    if (prevIsFocused === null) return;

    // restore previous state on blur, if it got out of sync
    if (state.inputValue !== (field.value?.label ?? '')) {
      setInnerSearchResult(field.value ? [field.value] : []);
      setSearchValue(field.value?.label ?? '');
      state.setSelectedKey(field.value?.value ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isFocused]);

  const {
    inputProps,
    listBoxProps,
    buttonProps: triggerProps,
  } = useComboBox(
    {
      placeholder,
      label,
      children: comboxBoxChildren,
      inputRef,
      buttonRef,
      id,
      listBoxRef,
      popoverRef,
    },
    state
  );

  const mode = useFormMode();
  const isForm = mode === 'regular';

  const focusRef = useFocusManager();

  const { buttonProps } = useButton(triggerProps, buttonRef);

  useEffect(() => {
    if (
      !state.isOpen &&
      keepOpenIfFocused &&
      document.activeElement === inputRef.current
    )
      state.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen, keepOpenIfFocused]);

  const opened = state.isOpen && !disabled;

  const floating = useFloating({
    placement: 'bottom-start',
    // padding added to keep some space between the flyout and viewport edges
    middleware: [shift({ padding: { left: 10, right: 10 } })],
  });

  return (
    <div>
      <InnerAddon
        left={leftAddon}
        right={
          <Stack flow="column" gap={0.5}>
            {selectedItem && isForm && (
              <RemoveButton
                error={error}
                tabIndex={-1}
                disabled={disabled}
                data-testid={`${name}-remove-selection`}
                onClick={() => {
                  helper.setValue({
                    value: '',
                    label: '',
                  });
                  state.setSelectedKey('');
                }}
                style={{ pointerEvents: 'initial' }}
              >
                <Stack flow="column" gap={0.5}>
                  <FontAwesomeIcon icon={faTimes} />
                  <Separator
                    orientation="vertical"
                    error={error}
                    disabled={disabled}
                  />
                </Stack>
              </RemoveButton>
            )}

            <SelectToggle
              {...buttonProps}
              data-testid={`${dataTestId ?? name}-togglebutton`}
              error={error}
              disabled={disabled}
              style={{ pointerEvents: 'initial' }}
            >
              <FontAwesomeIcon icon={state.isOpen ? faCaretUp : faCaretDown} />
            </SelectToggle>
          </Stack>
        }
      >
        {({ leftBounds, rightBounds }) => (
          <>
            {/*
              keep hidden input for compability with regular single select,
              so you can easily switch to the component without changing test code
            */}
            <input
              type="hidden"
              data-testid={dataTestId ?? name}
              value={field.value?.value ?? ''}
            />

            <SimpleInput
              ref={mergeRefs([floating.refs.setReference, focusRef, inputRef])}
              {...inputProps}
              aria-label={name}
              leftBounds={leftBounds}
              rightBounds={rightBounds}
              onFocus={(e) => {
                onFocus?.(e);
                inputProps.onFocus?.(e);
                state.setOpen(true);
              }}
              onBlur={(e) => {
                inputProps.onBlur?.(e);
                // de-focus if we don't move focus to popover
                if (!popoverRef.current?.contains(e.relatedTarget))
                  state.setFocused(false);
              }}
              onClick={(e) => {
                inputProps.onClick?.(e);
                state.setOpen(true);
              }}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              placeholder={placeholder}
              error={error}
              disabled={disabled}
              data-testid={`${dataTestId ?? name}Search`}
              // if we have focus, the field is on edit mode and we show the input search value.
              // if not we'll show the label of the selected item.
              // (in general the inputValue would be restored to the of the selected item
              // during blur, but there's like on render loop with an empty inputValue
              // and we'd see the placeholder flickering up in this moment.)
              value={
                state.isFocused ? state.inputValue : field.value?.label ?? ''
              }
            />
          </>
        )}
      </InnerAddon>

      {opened ? (
        <Popover
          onClose={state.close}
          ref={mergeRefs([popoverRef, floating.refs.setFloating])}
          style={{
            zIndex: 1,
            width: floating.refs.domReference.current
              ? `${floating.refs.domReference.current.clientWidth}px`
              : undefined,
            ...popoverStyle,
            ...floating.style,
          }}
        >
          {children({
            searchState: getSearchState({
              state,
              pending,
              minLength,
              inputValue: state.inputValue,
              lastSearchQuery:
                lastSearchQuery === undefined
                  ? state.inputValue
                  : lastSearchQuery,
            }),
            listBoxOptions: listBoxProps,
            comboBoxState: state,
            name,
            truncated,
            listBoxRef,
          })}
        </Popover>
      ) : null}
    </div>
  );
}

const SelectPopover: FC<ComboboxChildrenProps> = ({
  searchState,
  comboBoxState,
  listBoxOptions,
  name,
  truncated,
}) => {
  return (
    <SelectSearchStates searchState={searchState}>
      <ListBox
        state={comboBoxState}
        ariaListBoxProps={listBoxOptions}
        name={name}
        prepend={
          truncated && (
            <Alert type="warning">
              There are more than 100 results. Please refine your search
              criteria.
            </Alert>
          )
        }
      />
    </SelectSearchStates>
  );
};

type SearchStateInput<Value extends string = string> = {
  state: ListState<SelectOption<Value>>;
  pending?: boolean;
  minLength: number;
  inputValue: string | null;
  lastSearchQuery?: string | null;
};

type SearchState =
  | {
      case: 'WAIT_FOR_INPUT';
      minLength: number;
    }
  | { case: 'PENDING' }
  | { case: 'SUCCESS' }
  | { case: 'EMPTY'; lastSearchQuery: string };

function getSearchState(props: SearchStateInput): SearchState {
  const { state, pending, inputValue, lastSearchQuery, minLength } = props;

  const options = [...state.collection];

  const overMinLength = inputValue !== null && inputValue.length >= minLength;

  if (!pending && !overMinLength) return { case: 'WAIT_FOR_INPUT', minLength };

  if (pending || (lastSearchQuery == null && overMinLength))
    return { case: 'PENDING' };
  else {
    if (overMinLength && !!options.length) return { case: 'SUCCESS' };

    if (lastSearchQuery != null && overMinLength && !options.length)
      return { case: 'EMPTY', lastSearchQuery };
  }

  throw new Error('Unhandled search state in select.');
}

type SelectSearchStatesProps = {
  searchState: SearchState;
  children: ReactNode;
};

/**
 * This is a simple component to handle the search states for generic searchable selects.
 * More sophisticated searchable selects should handle the search states on their own.
 */
export const SelectSearchStates: FC<SelectSearchStatesProps> = ({
  searchState,
  children,
}) => {
  switch (searchState.case) {
    case 'WAIT_FOR_INPUT':
      return (
        <ListBoxContainer>
          <ListItem>
            <EmptyValue
              label={`Type at least ${searchState.minLength} ${
                searchState.minLength > 1 ? 'characters' : 'character'
              } to search...`}
            />
          </ListItem>
        </ListBoxContainer>
      );
    case 'PENDING':
      return (
        <ListBoxContainer>
          <ListItem>
            <EmptyValue label="Loading suggestions..." />
          </ListItem>
        </ListBoxContainer>
      );
    case 'SUCCESS':
      return <>{children}</>;
    case 'EMPTY':
      return (
        <ListBoxContainer>
          <ListItem>
            <EmptyValue
              label={`Could not find ${searchState.lastSearchQuery}.`}
            />
          </ListItem>
        </ListBoxContainer>
      );
  }
};
